document.documentElement.classList.remove('no-js');

const initMobileMenu = (root = document) => {
  const toggle = root.querySelector('[data-menu-toggle]');
  const menu = root.querySelector('[data-mobile-menu]');
  if (!toggle || !menu || toggle.dataset.initialized === 'true') return;

  toggle.dataset.initialized = 'true';
  toggle.addEventListener('click', () => {
    const open = menu.classList.toggle('is-open');
    document.body.classList.toggle('menu-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  });

  menu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      menu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      toggle.setAttribute('aria-expanded', 'false');
    });
  });
};

const initProductGallery = (gallery) => {
  if (!gallery || gallery.dataset.initialized === 'true') return;

  const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
  const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
  const previousButton = gallery.querySelector('[data-gallery-prev]');
  const nextButton = gallery.querySelector('[data-gallery-next]');
  const counter = gallery.querySelector('[data-gallery-current]');
  const zoomButton = gallery.querySelector('[data-gallery-zoom]');
  const dialog = gallery.querySelector('[data-gallery-dialog]');
  const dialogImage = gallery.querySelector('[data-gallery-dialog-image]');
  const dialogClose = gallery.querySelector('[data-gallery-dialog-close]');

  if (!slides.length) return;
  gallery.dataset.initialized = 'true';
  let activeIndex = 0;

  const activate = (requestedIndex) => {
    activeIndex = (requestedIndex + slides.length) % slides.length;

    slides.forEach((slide, index) => {
      const active = index === activeIndex;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
    });

    thumbs.forEach((thumb, index) => {
      const active = index === activeIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', String(active));
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });

    if (counter) counter.textContent = String(activeIndex + 1);
  };

  thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activate(index)));
  previousButton?.addEventListener('click', () => activate(activeIndex - 1));
  nextButton?.addEventListener('click', () => activate(activeIndex + 1));

  const stage = gallery.querySelector('.product-gallery__stage');
  let swipeStartX = 0;
  let swipeStartY = 0;
  let swipeActive = false;

  stage?.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    swipeStartX = event.touches[0].clientX;
    swipeStartY = event.touches[0].clientY;
    swipeActive = true;
  }, { passive: true });

  stage?.addEventListener('touchend', (event) => {
    if (!swipeActive || !event.changedTouches.length) return;
    swipeActive = false;

    const deltaX = event.changedTouches[0].clientX - swipeStartX;
    const deltaY = event.changedTouches[0].clientY - swipeStartY;
    const threshold = Math.max(42, (stage.clientWidth || 0) * 0.12);

    if (Math.abs(deltaX) < threshold || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
    activate(deltaX < 0 ? activeIndex + 1 : activeIndex - 1);
  }, { passive: true });

  stage?.addEventListener('touchcancel', () => {
    swipeActive = false;
  }, { passive: true });

  gallery.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') activate(activeIndex - 1);
    if (event.key === 'ArrowRight') activate(activeIndex + 1);
  });

  zoomButton?.addEventListener('click', () => {
    const activeImage = slides[activeIndex]?.querySelector('img');
    if (!activeImage || !dialog || !dialogImage) return;
    dialogImage.src = slides[activeIndex]?.dataset.galleryZoomSrc || activeImage.currentSrc || activeImage.src;
    dialogImage.alt = activeImage.alt;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  });

  dialogClose?.addEventListener('click', () => dialog?.close());
  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
};

const initProductForm = (form) => {
  if (!form || form.dataset.initialized === 'true') return;
  form.dataset.initialized = 'true';

  const section = form.closest('[data-section-id]');
  const select = form.querySelector('[data-variant-select]');
  const price = section?.querySelector('[data-product-price]');
  const comparePrice = section?.querySelector('[data-compare-price]');
  const addButton = form.querySelector('[data-add-to-cart]');
  const buyButtons = Array.from(section?.querySelectorAll('[data-buy-now]') || []);
  const mainBuyButton = form.querySelector('[data-buy-now]');
  const stickyBar = section?.querySelector('[data-sticky-buy-bar]');
  const stickyPrice = section?.querySelector('[data-sticky-price]');
  const buyNowStatus = form.querySelector('[data-buy-now-status]');
  const quantityInput = form.querySelector('[data-quantity-input]');
  const minusButton = form.querySelector('[data-quantity-minus]');
  const plusButton = form.querySelector('[data-quantity-plus]');
  const bundlePicker = form.querySelector('[data-bundle-picker]');
  const bundleColorMixer = form.querySelector('[data-bundle-color-mixer]');
  const bundleColorRows = form.querySelector('[data-bundle-color-rows]');
  const bundleColorStatus = form.querySelector('[data-bundle-color-status]');
  const isComfortLift = section?.classList.contains('main-product--comfortlift');
  const isHomeReset = section?.classList.contains('main-product--home-reset');
  const optionPickers = form.querySelector('[data-option-pickers]');
  const variantsJson = form.querySelector('[data-product-variants]');
  let productVariants = [];
  let bundleColorChoices = [];
  try { productVariants = variantsJson ? JSON.parse(variantsJson.textContent) : []; } catch (_error) { productVariants = []; }

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const getQuantity = () => Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);

  const getOptionGroups = () => Array.from(optionPickers?.querySelectorAll('[data-option-index]') || []);
  const getColorGroup = () => getOptionGroups().find((group) => {
    const label = group.querySelector('legend span')?.textContent?.trim().toLowerCase() || '';
    return label.includes('color') || label.includes('colour');
  });
  const getColorOptionIndex = () => {
    const group = getColorGroup();
    return group ? Number.parseInt(group.dataset.optionIndex || '-1', 10) : -1;
  };
  const getChosenOptions = () => getOptionGroups().map((group) => group.querySelector('[data-option-value]:checked')?.value);
  const getColorValues = () => {
    const colorIndex = getColorOptionIndex();
    if (colorIndex < 0) return [];
    return [...new Set(productVariants.map((variant) => variant.options[colorIndex]).filter(Boolean))];
  };
  const findVariantForColor = (color) => {
    const colorIndex = getColorOptionIndex();
    const chosen = getChosenOptions();
    if (colorIndex < 0 || chosen.some((value, index) => index !== colorIndex && !value)) return null;
    chosen[colorIndex] = color;
    return productVariants.find((variant) => variant.options.every((value, index) => value === chosen[index])) || null;
  };

  const getBundleItems = () => {
    const quantity = getQuantity();
    if (!isComfortLift || quantity === 1 || getColorOptionIndex() < 0) {
      const id = Number.parseInt(select?.value || '', 10);
      return id ? [{ id, quantity }] : [];
    }

    const grouped = new Map();
    bundleColorChoices.slice(0, quantity).forEach((color) => {
      const variant = findVariantForColor(color);
      if (!variant?.available) return;
      grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1);
    });
    return Array.from(grouped, ([id, itemQuantity]) => ({ id: Number(id), quantity: itemQuantity }));
  };

  const updateDisplayedPrice = () => {
    const quantity = getQuantity();
    const unitPrice = Number.parseInt(price?.dataset.unitPrice || '0', 10) || 0;
    const unitComparePrice = Number.parseInt(comparePrice?.dataset.unitComparePrice || '0', 10) || 0;
    const comfortLiftTotals = { 1: 3999, 2: 6998, 3: 8997 };
    const calculatedTotal = isComfortLift && unitPrice === 3999 && comfortLiftTotals[quantity] ? comfortLiftTotals[quantity] : unitPrice * quantity;
    const totalPrice = formatMoney(calculatedTotal);

    if (quantityInput && quantityInput.value !== String(quantity)) quantityInput.value = String(quantity);
    if (price) price.textContent = totalPrice;
    if (stickyPrice) stickyPrice.textContent = totalPrice;

    if (comparePrice) {
      const hasComparePrice = unitComparePrice > unitPrice;
      comparePrice.textContent = hasComparePrice ? formatMoney(unitComparePrice * quantity) : '';
      comparePrice.hidden = !hasComparePrice;
    }
  };

  const setBuyButtons = ({ available, loading = false }) => {
    buyButtons.forEach((button) => {
      button.disabled = !available || loading;
      button.setAttribute('aria-busy', String(loading));
      const label = button.querySelector('[data-buy-now-label]');
      if (label) label.textContent = loading ? 'OPENING CHECKOUT…' : available ? (isHomeReset ? 'GET INSTANT ACCESS' : 'BUY NOW') : 'SOLD OUT';
    });
  };

  const syncBundleAvailability = () => {
    if (!isComfortLift || getQuantity() <= 1 || getColorOptionIndex() < 0) return true;
    const items = getBundleItems();
    const selectedCount = items.reduce((total, item) => total + item.quantity, 0);
    const available = selectedCount === getQuantity();
    if (addButton) {
      addButton.disabled = !available;
      addButton.textContent = available ? 'ADD TO CART' : 'SELECT AVAILABLE COLORS';
    }
    setBuyButtons({ available });
    if (bundleColorStatus) bundleColorStatus.textContent = available ? '' : 'One of these color and size combinations is currently unavailable.';
    return available;
  };

  const renderBundleColorMixer = () => {
    if (!isComfortLift || !bundleColorMixer || !bundleColorRows) return;
    const quantity = getQuantity();
    const colorGroup = getColorGroup();
    const colorValues = getColorValues();
    const currentColor = colorGroup?.querySelector('[data-option-value]:checked')?.value || colorValues[0];

    if (quantity <= 1 || !colorGroup || colorValues.length < 2) {
      bundleColorMixer.hidden = true;
      colorGroup?.removeAttribute('hidden');
      bundleColorChoices = currentColor ? [currentColor] : [];
      if (bundleColorStatus) bundleColorStatus.textContent = '';
      return;
    }

    colorGroup.hidden = true;
    bundleColorMixer.hidden = false;
    const availableColors = colorValues.filter((color) => findVariantForColor(color)?.available);
    const previousLength = bundleColorChoices.length;
    bundleColorChoices = bundleColorChoices.slice(0, quantity);

    while (bundleColorChoices.length < quantity) {
      const index = bundleColorChoices.length;
      const alternate = availableColors.find((color) => color !== currentColor);
      const preferred = index === 1 && alternate ? alternate : currentColor;
      bundleColorChoices.push(availableColors.includes(preferred) ? preferred : availableColors[0] || colorValues[0]);
    }

    bundleColorChoices = bundleColorChoices.map((color, index) => {
      if (availableColors.includes(color)) return color;
      if (index >= previousLength && index === 1) return availableColors.find((value) => value !== currentColor) || availableColors[0] || colorValues[0];
      return availableColors.includes(currentColor) ? currentColor : availableColors[0] || colorValues[0];
    });

    bundleColorRows.replaceChildren();
    bundleColorChoices.forEach((selectedColor, index) => {
      const row = document.createElement('div');
      row.className = 'bundle-color-row';
      const label = document.createElement('label');
      const selectId = `BundleColor-${section?.dataset.sectionId || 'product'}-${index + 1}`;
      label.htmlFor = selectId;
      label.textContent = `Bra ${index + 1}`;
      const colorSelect = document.createElement('select');
      colorSelect.id = selectId;
      colorSelect.className = 'bundle-color-select';
      colorSelect.setAttribute('aria-label', `Choose color for bra ${index + 1}`);

      colorValues.forEach((color) => {
        const option = document.createElement('option');
        const variant = findVariantForColor(color);
        option.value = color;
        option.textContent = variant?.available ? color : `${color} — Sold out`;
        option.disabled = !variant?.available;
        option.selected = color === selectedColor;
        colorSelect.append(option);
      });

      colorSelect.addEventListener('change', () => {
        bundleColorChoices[index] = colorSelect.value;
        syncBundleAvailability();
      });
      row.append(label, colorSelect);
      bundleColorRows.append(row);
    });

    syncBundleAvailability();
  };

  const updateStickyBar = () => {
    if (!stickyBar || !mainBuyButton || !section) return;

    const buttonRect = mainBuyButton.getBoundingClientRect();
    const passedMainButton = buttonRect.bottom < 78;
    const shouldShow = passedMainButton && !mainBuyButton.disabled;

    stickyBar.hidden = !shouldShow;
    stickyBar.classList.toggle('is-visible', shouldShow);
    document.body.classList.toggle('has-sticky-buy-now', shouldShow);
  };

  const updateVariant = () => {
    const option = select?.selectedOptions?.[0];
    if (!option) return;
    if (price) price.dataset.unitPrice = option.dataset.priceCents || '0';
    if (comparePrice) {
      const selectedUnitPrice = Number.parseInt(option.dataset.priceCents || '0', 10) || 0;
      comparePrice.dataset.unitComparePrice = isComfortLift ? String(selectedUnitPrice * 2) : option.dataset.comparePriceCents || '0';
    }
    updateDisplayedPrice();

    if (addButton) {
      const available = option.dataset.available === 'true';
      addButton.disabled = !available;
      addButton.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      setBuyButtons({ available });
    }

    renderBundleColorMixer();
    updateStickyBar();
  };

  const updateVariantFromPills = () => {
    if (!optionPickers || !select || !productVariants.length) return;
    const groups = Array.from(optionPickers.querySelectorAll('[data-option-index]'));
    const chosen = groups.map((group) => group.querySelector('[data-option-value]:checked')?.value);
    if (chosen.some((value) => !value)) return;
    const match = productVariants.find((variant) => variant.options.every((value, index) => value === chosen[index]));
    if (!match) return;
    select.value = String(match.id);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const addComfortLiftBundle = async () => {
    const items = getBundleItems();
    const selectedCount = items.reduce((total, item) => total + item.quantity, 0);
    if (!items.length || selectedCount !== getQuantity()) throw new Error('Please choose an available color for every bra.');
    const routesRoot = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'This bundle could not be added.');
    }
    return routesRoot;
  };

  const buyNow = async () => {
    if (!mainBuyButton || mainBuyButton.disabled) return;

    const quantity = getQuantity();
    if (quantityInput) quantityInput.value = String(quantity);

    setBuyButtons({ available: true, loading: true });
    if (buyNowStatus) buyNowStatus.textContent = 'Opening secure checkout…';

    try {
      const routesRoot = window.Shopify?.routes?.root || '/';
      if (isComfortLift) {
        await addComfortLiftBundle();
      } else {
        const response = await fetch(`${routesRoot}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'This item could not be added.');
        }
      }

      window.location.assign(`${routesRoot}checkout`);
    } catch (error) {
      const option = select?.selectedOptions?.[0];
      const available = option ? option.dataset.available === 'true' : true;
      setBuyButtons({ available });
      if (buyNowStatus) buyNowStatus.textContent = error.message || 'Please try again.';
      updateStickyBar();
    }
  };

  select?.addEventListener('change', updateVariant);
  optionPickers?.querySelectorAll('[data-option-value]').forEach((input) => input.addEventListener('change', updateVariantFromPills));
  bundlePicker?.querySelectorAll('[data-bundle-quantity]').forEach((input) => input.addEventListener('change', () => {
    if (!input.checked || !quantityInput) return;
    quantityInput.value = input.value;
    updateDisplayedPrice();
    renderBundleColorMixer();
  }));
  minusButton?.addEventListener('click', () => {
    quantityInput?.stepDown();
    updateDisplayedPrice();
  });
  plusButton?.addEventListener('click', () => {
    quantityInput?.stepUp();
    updateDisplayedPrice();
  });
  quantityInput?.addEventListener('input', updateDisplayedPrice);
  quantityInput?.addEventListener('change', updateDisplayedPrice);
  form.addEventListener('submit', async (event) => {
    if (!isComfortLift) return;
    event.preventDefault();
    if (!addButton || addButton.disabled) return;
    const originalLabel = addButton.textContent;
    addButton.disabled = true;
    addButton.textContent = 'ADDING…';
    if (buyNowStatus) buyNowStatus.textContent = 'Adding your selected bundle…';
    try {
      const routesRoot = await addComfortLiftBundle();
      window.location.assign(`${routesRoot}cart`);
    } catch (error) {
      addButton.disabled = false;
      addButton.textContent = originalLabel;
      if (buyNowStatus) buyNowStatus.textContent = error.message || 'Please try again.';
      syncBundleAvailability();
    }
  });
  buyButtons.forEach((button) => button.addEventListener('click', buyNow));
  window.addEventListener('scroll', updateStickyBar, { passive: true });
  window.addEventListener('resize', updateStickyBar);
  updateDisplayedPrice();
  renderBundleColorMixer();
  updateStickyBar();
};

const initSizeGuide = (root) => {
  const dialog = root.querySelector?.('[data-size-guide-dialog]') || document.querySelector('[data-size-guide-dialog]');
  if (!dialog || dialog.dataset.initialized === 'true') return;
  dialog.dataset.initialized = 'true';
  document.querySelectorAll('[data-size-guide-open]').forEach((button) => button.addEventListener('click', () => {
    if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
  }));
  dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
};

const initTheme = (root = document) => {
  initMobileMenu(root);
  root.querySelectorAll('[data-product-gallery]').forEach(initProductGallery);
  root.querySelectorAll('[data-product-form]').forEach(initProductForm);
  initSizeGuide(root);
};

document.addEventListener('DOMContentLoaded', () => initTheme());
document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
